import { formatNameForDisplay } from "@/common/helpers/utils";
import { useTranslation } from "react-i18next";
import { BaseConfirmDialogProps, ConfirmationType } from "../_types";
import { BaseConfirmDialog } from "../base-confirm-dialog";
import { ButtonVariant, ButtonColorVariant } from "@/components/ui/button";

interface DeleteConfirmDialogProps extends BaseConfirmDialogProps {
  label: string;
  name: string;
  confirmationType?: ConfirmationType;
  showIrreversibleWarning?: boolean;
}

export function DeleteConfirmDialog({
  open,
  label,
  name,
  onCancel,
  onConfirm,
  requireConfirmation = false,
  confirmationType = ConfirmationType.Keyword,
  showIrreversibleWarning = false,
  title,
  description: customDescription,
  labelCancel,
  labelConfirm,
  confirmationPlaceholder,
  confirmationLabel: customConfirmationLabel,
  expectedConfirmationText: customExpectedConfirmationText,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation("common");
  const safeName = formatNameForDisplay(name || "", 50);
  const safeConfirmationName = formatNameForDisplay(name || "", 30);

  const baseDescription = t("ConfirmDeleteResource", { label, name: safeName });

  const defaultDescription = showIrreversibleWarning ? (
    <div className="space-y-2">
      <div className="text-error text-sm italic">{t("ActionCannotBeUndone")}</div>
    </div>
  ) : (
    baseDescription
  );

  const defaultExpectedConfirmation = confirmationType === ConfirmationType.Keyword ? t("ram:Delete") : name;
  const defaultConfirmationLabel =
    confirmationType === ConfirmationType.Keyword
      ? t("ConfirmWithDelete")
      : t("EnterNameToConfirm", { name: safeConfirmationName });

  return (
    <BaseConfirmDialog
      open={open}
      title={title ?? t("ConfirmDeletion", { name: safeConfirmationName })}
      description={customDescription ?? defaultDescription}
      labelCancel={labelCancel ?? t("NoCancel")}
      labelConfirm={labelConfirm ?? t("ram:YesDelete")}
      onCancel={onCancel}
      onConfirm={onConfirm}
      requireConfirmation={requireConfirmation}
      confirmationPlaceholder={confirmationPlaceholder ?? defaultExpectedConfirmation}
      confirmationLabel={customConfirmationLabel ?? defaultConfirmationLabel}
      expectedConfirmationText={customExpectedConfirmationText ?? defaultExpectedConfirmation}
      cancelVariant={ButtonVariant.Outlined}
      confirmColorVariant={ButtonColorVariant.Error}
    />
  );
}
